import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { Link } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import {
  AlertCircle,
  BadgeCheck,
  Bell,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  CalendarRange,
  Camera,
  CheckCircle2,
  ChevronRight,
  CircleDot,
  Clock3,
  ImagePlus,
  ListTodo,
  Loader2,
  PencilLine,
  Plane,
  Save,
  Shield,
  Sparkles,
  UserRound,
  X,
  type LucideIcon,
} from "lucide-react";

import { getRrhhData } from "@modules/rrhh/api/rrhh";
import type { RrhhData } from "@modules/rrhh/types/rrhh";
import { getTasksData } from "@modules/tasks/api/tasks";
import type { Task, TaskIndexResponse } from "@modules/tasks/types/tasks";
import { api } from "@shared/api/client";
import type { AuthMe, UpdateProfilePayload } from "@shared/api/auth";
import { useAuthStore } from "@shared/store/auth";
import "@modules/admin-panel/styles/admin-panel.css";
import "../styles/profile.css";

type ProfileFormState = {
  nombre: string;
  fecha_nacimiento: string;
  contrasena: string;
  foto: File | null;
};

type AccessSummary = {
  label: string;
  level: "Consulta" | "Gestión";
};

function buildProfileForm(me: AuthMe | null): ProfileFormState {
  return {
    nombre: me?.user.name ?? "",
    fecha_nacimiento: me?.user.fecha_nacimiento ?? "",
    contrasena: "",
    foto: null,
  };
}

function getInitials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((value) => value.slice(0, 1).toUpperCase())
      .join("") || "U"
  );
}

function formatDate(value?: string | null): string {
  if (!value) return "Sin registrar";

  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T12:00:00`)
    : new Date(value);

  if (Number.isNaN(date.getTime())) return "Sin registrar";

  return date.toLocaleDateString("es-ES");
}

function formatDateTime(value?: string | null): string {
  if (!value) return "Sin registrar";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Sin registrar";

  return date.toLocaleString("es-ES", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function localIsoDate(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function formatDays(value?: number | null): string {
  if (value === null || value === undefined) return "—";

  const formatted = Number.isInteger(value)
    ? String(value)
    : value.toLocaleString("es-ES", { maximumFractionDigits: 1 });

  return `${formatted} d`;
}

function formatWorkedTime(seconds?: number | null): string {
  if (seconds === null || seconds === undefined) return "—";

  const safeSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);

  return `${hours} h ${String(minutes).padStart(2, "0")} min`;
}

function clockStatusLabel(data: RrhhData | null): string {
  const clock = data?.clock;
  if (!clock) return "No disponible";
  if (clock.is_vacation_day) return "Vacaciones";

  switch (clock.status) {
    case "abierto":
      return "Jornada activa";
    case "cerrado":
      return "Jornada finalizada";
    case "autocerrado":
      return "Jornada autocerrada";
    default:
      return "Pendiente de fichar";
  }
}

function taskDueLabel(task: Task): string {
  if (!task.fecha_fin) return "Sin vencimiento";
  return formatDate(task.fecha_fin);
}

function buildAccessSummary(permissions: string[]): AccessSummary[] {
  const modules = [
    { label: "Certificaciones", tokens: ["cert", "quality", "iso"] },
    { label: "Tareas", tokens: ["task"] },
    { label: "Recursos Humanos", tokens: ["rrhh", "vacation", "payroll", "clock"] },
    { label: "IT", tokens: ["it-", "incident"] },
    { label: "Proyectos", tokens: ["project"] },
    { label: "Facturación", tokens: ["invoice", "billing"] },
    { label: "Contabilidad", tokens: ["account", "journal", "ledger"] },
    { label: "Compras", tokens: ["purchase"] },
    { label: "Configuración", tokens: ["role", "setting", "issuer", "team"] },
  ];

  const managementTokens = [
    "manage-",
    "create-",
    "edit-",
    "delete-",
    "assign-",
    "approve-",
    "sync-",
    "update-",
  ];

  return modules.flatMap((module) => {
    const matched = permissions.filter((permission) => {
      const value = permission.toLowerCase();
      return module.tokens.some((token) => value.includes(token));
    });

    if (matched.length === 0) return [];

    const canManage = matched.some((permission) =>
      managementTokens.some((token) => permission.toLowerCase().includes(token)),
    );

    return [{ label: module.label, level: canManage ? "Gestión" : "Consulta" }];
  });
}

type DetailCardProps = {
  icon: LucideIcon;
  label: string;
  value: string;
};

function DetailCard({ icon: Icon, label, value }: DetailCardProps) {
  return (
    <div className="group relative overflow-hidden rounded-[24px] border border-[#dce9e3] bg-white/85 p-4 shadow-[0_14px_36px_rgba(15,23,42,.045)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-[0_20px_46px_rgba(15,23,42,.08)]">
      <div className="relative flex items-center gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-emerald-100 bg-[linear-gradient(135deg,#ecfdf5,#f0fdfa)] text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.08)]">
          <Icon size={17} />
        </span>

        <div className="min-w-0">
          <div className="text-[10px] font-black uppercase tracking-[0.17em] text-slate-400">
            {label}
          </div>
          <div className="mt-1 break-words text-sm font-black leading-5 text-slate-950">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

type SummaryCardProps = {
  icon: LucideIcon;
  label: string;
  value: string;
  detail: string;
};

function SummaryCard({ icon: Icon, label, value, detail }: SummaryCardProps) {
  return (
    <div className="relative overflow-hidden rounded-[26px] border border-[#dce9e3] bg-white/88 p-5 shadow-[0_16px_42px_rgba(15,23,42,.055)] backdrop-blur-xl">
      <div className="absolute -right-12 -top-12 h-28 w-28 rounded-full bg-emerald-300/15 blur-3xl" />
      <div className="relative flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
            {label}
          </div>
          <div className="mt-2 text-2xl font-black tracking-tight text-slate-950">
            {value}
          </div>
          <div className="mt-1 truncate text-xs font-bold text-slate-500" title={detail}>
            {detail}
          </div>
        </div>
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700">
          <Icon size={18} />
        </span>
      </div>
    </div>
  );
}

type SectionProps = {
  title: string;
  subtitle: string;
  icon: LucideIcon;
  children: ReactNode;
  action?: ReactNode;
};

function Section({ title, subtitle, icon: Icon, children, action }: SectionProps) {
  return (
    <section className="overflow-hidden rounded-[32px] border border-[#d7e5df] bg-white/82 shadow-[0_20px_58px_rgba(15,23,42,.065)] backdrop-blur-xl transition-all duration-300 hover:border-emerald-200 hover:shadow-[0_28px_72px_rgba(15,23,42,.09)]">
      <div className="border-b border-[#edf1f3] bg-[linear-gradient(135deg,#ffffff,#f4fbf8)] px-5 py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[linear-gradient(135deg,#10b981,#14b8a6)] text-white shadow-[0_12px_28px_rgba(16,185,129,.20)]">
              <Icon size={18} />
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-black tracking-tight text-slate-950">{title}</h2>
              <p className="mt-0.5 text-xs font-semibold text-slate-500">{subtitle}</p>
            </div>
          </div>
          {action}
        </div>
      </div>

      <div className="p-5">{children}</div>
    </section>
  );
}

function DataRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-slate-100 py-3 last:border-b-0">
      <span className="text-sm font-semibold text-slate-500">{label}</span>
      <span className="text-right text-sm font-black text-slate-900">{value}</span>
    </div>
  );
}

export function ProfilePage() {
  const me = useAuthStore((state) => state.me);
  const updateProfile = useAuthStore((state) => state.updateProfile);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [form, setForm] = useState<ProfileFormState>(() => buildProfileForm(me));
  const [rrhh, setRrhh] = useState<RrhhData | null>(null);
  const [tasks, setTasks] = useState<TaskIndexResponse | null>(null);
  const [unreadNotifications, setUnreadNotifications] = useState<number | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  useEffect(() => {
    if (!editing) {
      setForm(buildProfileForm(me));
      setPreviewUrl(null);
    }
  }, [editing, me]);

  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    let cancelled = false;

    const loadSummary = async () => {
      setSummaryLoading(true);

      const [rrhhResult, tasksResult, notificationsResult] = await Promise.allSettled([
        getRrhhData(),
        getTasksData(),
        api.get<{ no_leidas: number }>("/notifications/unread-count"),
      ]);

      if (cancelled) return;

      if (rrhhResult.status === "fulfilled") {
        setRrhh(rrhhResult.value);
      }

      if (tasksResult.status === "fulfilled") {
        setTasks(tasksResult.value);
      }

      if (notificationsResult.status === "fulfilled") {
        setUnreadNotifications(notificationsResult.value.data.no_leidas);
      }

      setSummaryLoading(false);
    };

    void loadSummary();

    return () => {
      cancelled = true;
    };
  }, []);

  const user = me?.user;
  const userName = user?.name ?? "Usuario";
  const avatarUrl = previewUrl ?? user?.foto_url ?? null;
  const initials = getInitials(userName);
  const today = localIsoDate();

  const completionItems = useMemo(
    () => [
      Boolean(user?.name),
      Boolean(user?.email),
      Boolean(user?.fecha_nacimiento),
      Boolean(user?.departamento),
      Boolean(user?.empresa),
      Boolean(user?.foto_url),
    ],
    [user],
  );

  const completion = Math.round(
    (completionItems.filter(Boolean).length / completionItems.length) * 100,
  );

  const vacationItems = rrhh?.vacations.items ?? [];
  const vacationSummary = rrhh?.vacations.summary;
  const pendingVacationDays = vacationItems
    .filter((item) => item.estado === "pendiente")
    .reduce((total, item) => total + Number(item.dias_solicitados || 0), 0);
  const nextApprovedVacation = [...vacationItems]
    .filter((item) => item.estado === "aprobado" && item.fecha_fin >= today)
    .sort((a, b) => a.fecha_inicio.localeCompare(b.fecha_inicio))[0] ?? null;

  const currentUserId = user?.id ?? 0;
  const taskItems = (tasks?.data ?? []).filter((task) =>
    task.asignado_id === currentUserId
    || task.creado_por === currentUserId
    || task.participantes.some((participant) => participant.usuario_id === currentUserId),
  );
  const openTasks = taskItems.filter((task) => task.estado !== "completado");
  const overdueTasks = openTasks.filter((task) => Boolean(task.fecha_fin && task.fecha_fin < today));
  const todayTasks = openTasks.filter((task) => task.fecha_fin === today);
  const nextTasks = [...openTasks]
    .sort((a, b) => {
      const aDate = a.fecha_fin ?? a.fecha_inicio ?? "9999-12-31";
      const bDate = b.fecha_fin ?? b.fecha_inicio ?? "9999-12-31";
      return aDate.localeCompare(bDate) || a.id - b.id;
    })
    .slice(0, 3);

  const accessSummary = useMemo(
    () => buildAccessSummary(me?.permissions ?? []),
    [me?.permissions],
  );

  const scheduleLabel = rrhh?.clock.effective_schedule_label
    ?? (rrhh?.clock.schedule
      ? `${rrhh.clock.schedule.hora_inicio.slice(0, 5)} - ${rrhh.clock.schedule.hora_fin.slice(0, 5)}`
      : "Sin horario asignado");

  const handleEdit = () => {
    setEditing(true);
    setError("");
    setForm(buildProfileForm(me));
  };

  const handleCancel = () => {
    setEditing(false);
    setSaving(false);
    setError("");
    setForm(buildProfileForm(me));
  };

  const handlePhotoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    setForm((current) => ({
      ...current,
      foto: file,
    }));

    setPreviewUrl((current) => {
      if (current?.startsWith("blob:")) {
        URL.revokeObjectURL(current);
      }

      return file ? URL.createObjectURL(file) : null;
    });
  };

  const handleSave = async () => {
    setError("");

    if (!form.nombre.trim()) {
      setError("El nombre es obligatorio.");
      return;
    }

    setSaving(true);

    try {
      const payload: UpdateProfilePayload = {
        nombre: form.nombre.trim(),
        contrasena: form.contrasena.trim() || undefined,
        foto: form.foto,
        fecha_nacimiento: form.fecha_nacimiento || null,
      };

      await updateProfile(payload);
      setEditing(false);
    } catch (err) {
      if (isAxiosError(err)) {
        const responseMessage = (err.response?.data as { message?: string } | undefined)
          ?.message;

        setError(
          typeof responseMessage === "string"
            ? responseMessage
            : "No se pudo actualizar el perfil.",
        );
      } else {
        setError("No se pudo actualizar el perfil.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative p-5 lg:p-8">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute right-[-140px] top-[-160px] h-[420px] w-[420px] rounded-full bg-emerald-300/20 blur-3xl" />
        <div className="absolute bottom-[-180px] left-[8%] h-[360px] w-[360px] rounded-full bg-teal-300/16 blur-3xl" />
      </div>

      <div className="relative flex w-full max-w-none flex-col gap-6">
        <section className="relative overflow-hidden rounded-[38px] border border-white/80 bg-[linear-gradient(135deg,rgba(255,255,255,.94),rgba(238,250,244,.86))] p-6 shadow-[0_30px_90px_rgba(15,23,42,.11)] backdrop-blur-2xl lg:p-7">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-emerald-300/90 to-transparent" />
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-emerald-300/28 blur-3xl" />
            <div className="absolute left-1/2 top-[-160px] h-80 w-80 rounded-full bg-teal-300/16 blur-3xl" />
          </div>

          <div className="relative grid gap-7 xl:grid-cols-[1fr_320px] xl:items-center">
            <div className="flex flex-col gap-5 md:flex-row md:items-center">
              <div className="relative">
                <div className="absolute inset-[-9px] rounded-[38px] bg-[linear-gradient(135deg,#10b981,#14b8a6)] opacity-20 blur-xl" />
                <div className="relative grid h-32 w-32 place-items-center overflow-hidden rounded-[34px] border border-white bg-[linear-gradient(135deg,#10b981,#14b8a6)] text-4xl font-black text-white shadow-[0_24px_60px_rgba(16,185,129,.25)]">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={`Foto de ${userName}`} className="h-full w-full object-cover" />
                  ) : (
                    initials
                  )}
                </div>
                <span className="absolute -bottom-2 -right-2 grid h-10 w-10 place-items-center rounded-xl border border-white bg-white text-emerald-700 shadow-[0_10px_28px_rgba(15,23,42,.12)]">
                  <BadgeCheck size={19} />
                </span>
              </div>

              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50/90 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.17em] text-emerald-700">
                  <Sparkles size={13} />
                  Mi espacio
                </div>
                <h1 className="mt-3 text-4xl font-black tracking-[-0.06em] text-slate-950 lg:text-5xl">
                  {userName}
                </h1>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full border border-[#dfe8e4] bg-white/90 px-3 py-1.5 text-xs font-black text-slate-700">
                    <Shield size={14} className="text-emerald-600" />
                    {user?.rol ?? "empleado"}
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full border border-[#dfe8e4] bg-white/90 px-3 py-1.5 text-xs font-bold text-slate-600">
                    <BriefcaseBusiness size={14} className="text-emerald-600" />
                    {user?.departamento ?? "Sin departamento"}
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full border border-[#dfe8e4] bg-white/90 px-3 py-1.5 text-xs font-bold text-slate-600">
                    <Building2 size={14} className="text-emerald-600" />
                    {user?.empresa ?? "Sin empresa"}
                  </span>
                </div>
              </div>
            </div>

            <aside className="rounded-[28px] border border-[#d7e5df] bg-white/84 p-5 shadow-[0_16px_42px_rgba(15,23,42,.065)] backdrop-blur-xl">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">Perfil completo</div>
                  <div className="mt-1 text-3xl font-black text-slate-950">{completion}%</div>
                </div>
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-emerald-50 text-emerald-700">
                  <UserRound size={21} />
                </span>
              </div>
              <div className="mt-4 h-2.5 overflow-hidden rounded-full bg-slate-100 shadow-inner">
                <div className="h-full rounded-full bg-[linear-gradient(90deg,#10b981,#14b8a6)] transition-all duration-700" style={{ width: `${completion}%` }} />
              </div>
              <button
                type="button"
                onClick={editing ? handleCancel : handleEdit}
                className={[
                  "mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-black transition-all duration-300",
                  editing
                    ? "border border-[#d7e5df] bg-white text-slate-700 hover:bg-slate-50"
                    : "bg-[linear-gradient(135deg,#10b981,#14b8a6)] text-white shadow-[0_14px_32px_rgba(16,185,129,.22)] hover:-translate-y-0.5",
                ].join(" ")}
              >
                {editing ? <X size={17} /> : <PencilLine size={17} />}
                {editing ? "Cerrar edición" : "Editar perfil"}
              </button>
            </aside>
          </div>
        </section>

        {editing && (
          <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_330px]">
            <div className="overflow-hidden rounded-[34px] border border-[#d7e5df] bg-white/90 shadow-[0_24px_70px_rgba(15,23,42,.08)] backdrop-blur-xl">
              <div className="border-b border-[#edf1f3] bg-[linear-gradient(135deg,#ffffff,#f4fbf8)] px-6 py-5">
                <div className="flex items-center gap-4">
                  <span className="grid h-11 w-11 place-items-center rounded-xl bg-[linear-gradient(135deg,#10b981,#14b8a6)] text-white shadow-[0_14px_32px_rgba(16,185,129,.20)]">
                    <Camera size={19} />
                  </span>
                  <div>
                    <h2 className="text-lg font-black text-slate-950">Editar datos permitidos</h2>
                    <p className="text-sm font-semibold text-slate-500">Nombre, contraseña, foto y fecha de nacimiento.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-5 p-6">
                {error && (
                  <div className="flex items-start gap-3 rounded-[24px] border border-red-200 bg-red-50/90 px-4 py-3 text-sm font-semibold text-red-700">
                    <AlertCircle size={18} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="grid gap-5 md:grid-cols-2">
                  <label className="block">
                    <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">Nombre</span>
                    <input
                      type="text"
                      value={form.nombre}
                      onChange={(event) => setForm((current) => ({ ...current, nombre: event.target.value }))}
                      className="field-control"
                      disabled={saving}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">Fecha de nacimiento</span>
                    <input
                      type="date"
                      value={form.fecha_nacimiento}
                      onChange={(event) => setForm((current) => ({ ...current, fecha_nacimiento: event.target.value }))}
                      className="field-control"
                      disabled={saving}
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">Nueva contraseña</span>
                  <input
                    type="password"
                    value={form.contrasena}
                    onChange={(event) => setForm((current) => ({ ...current, contrasena: event.target.value }))}
                    className="field-control"
                    placeholder="Mínimo 8 caracteres"
                    disabled={saving}
                  />
                </label>

                <div className="rounded-[26px] border border-dashed border-emerald-200 bg-[linear-gradient(135deg,#ffffff,#f2fbf7)] p-5">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <div className="text-sm font-black text-slate-950">Foto de perfil</div>
                      <p className="mt-1 text-sm font-semibold text-slate-500">JPG, PNG o WEBP. Tamaño máximo: 4 MB.</p>
                    </div>
                    <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-2.5 text-sm font-black text-emerald-700 transition hover:bg-emerald-100">
                      <ImagePlus size={17} />
                      Seleccionar imagen
                      <input type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" disabled={saving} />
                    </label>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-xl bg-[linear-gradient(135deg,#10b981,#14b8a6)] px-5 py-2.5 text-sm font-black text-white shadow-[0_14px_32px_rgba(16,185,129,.22)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving ? <Loader2 size={17} className="animate-spin" /> : <Save size={17} />}
                    Guardar cambios
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-xl border border-[#d7e5df] bg-white/90 px-5 py-2.5 text-sm font-black text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <X size={17} />
                    Cancelar
                  </button>
                </div>
              </div>
            </div>

            <aside className="rounded-[34px] border border-[#d7e5df] bg-[linear-gradient(180deg,#ffffff_0%,#f2fbf7_100%)] p-6 shadow-[0_24px_70px_rgba(15,23,42,.07)]">
              <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">Vista previa</div>
              <div className="mt-6 flex flex-col items-center text-center">
                <div className="grid h-32 w-32 place-items-center overflow-hidden rounded-[34px] border border-white bg-[linear-gradient(135deg,#10b981,#14b8a6)] text-4xl font-black text-white shadow-[0_20px_52px_rgba(16,185,129,.22)]">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={`Vista previa de ${form.nombre || userName}`} className="h-full w-full object-cover" />
                  ) : (
                    getInitials(form.nombre || userName)
                  )}
                </div>
                <div className="mt-5 text-xl font-black tracking-tight text-slate-950">{form.nombre || "Tu nombre"}</div>
                <div className="mt-1 text-sm font-semibold text-slate-500">{user?.email ?? "Sin correo"}</div>
              </div>
            </aside>
          </section>
        )}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryCard
            icon={Plane}
            label="Vacaciones disponibles"
            value={summaryLoading && !vacationSummary ? "…" : formatDays(vacationSummary?.remaining_days)}
            detail={pendingVacationDays > 0 ? `${formatDays(pendingVacationDays)} pendientes de aprobación` : "Sin solicitudes pendientes"}
          />
          <SummaryCard
            icon={Clock3}
            label="Jornada de hoy"
            value={summaryLoading && !rrhh ? "…" : clockStatusLabel(rrhh)}
            detail={scheduleLabel}
          />
          <SummaryCard
            icon={ListTodo}
            label="Tareas pendientes"
            value={summaryLoading && !tasks ? "…" : String(openTasks.length)}
            detail={overdueTasks.length > 0 ? `${overdueTasks.length} vencidas` : "Sin tareas vencidas"}
          />
          <SummaryCard
            icon={Bell}
            label="Notificaciones"
            value={summaryLoading && unreadNotifications === null ? "…" : String(unreadNotifications ?? 0)}
            detail={(unreadNotifications ?? 0) === 1 ? "1 sin leer" : `${unreadNotifications ?? 0} sin leer`}
          />
        </div>

        <div className="grid gap-6 xl:grid-cols-2">
          <Section title="Mi jornada" subtitle="Estado y horario de hoy." icon={Clock3}>
            <div className="grid gap-4 sm:grid-cols-2">
              <DetailCard icon={CircleDot} label="Estado" value={clockStatusLabel(rrhh)} />
              <DetailCard icon={CalendarRange} label="Horario" value={scheduleLabel} />
              <DetailCard icon={CheckCircle2} label="Entrada" value={rrhh?.clock.entry_time?.slice(0, 5) ?? "Sin registrar"} />
              <DetailCard icon={Clock3} label="Salida" value={rrhh?.clock.exit_time?.slice(0, 5) ?? "Sin registrar"} />
            </div>
            <div className="mt-4 rounded-[22px] border border-emerald-100 bg-emerald-50/60 px-4 py-3">
              <DataRow label="Tiempo trabajado" value={formatWorkedTime(rrhh?.clock.seconds)} />
              <DataRow label="Fecha" value={formatDate(rrhh?.clock.today)} />
            </div>
          </Section>

          <Section
            title="Mis vacaciones"
            subtitle="Saldo y próximas ausencias."
            icon={Plane}
            action={(
              <Link to="/rrhh" className="inline-flex items-center gap-1 text-xs font-black text-emerald-700 hover:text-emerald-800">
                Ver RRHH <ChevronRight size={14} />
              </Link>
            )}
          >
            <div className="grid gap-4 sm:grid-cols-3">
              <DetailCard icon={CalendarDays} label="Disponibles" value={formatDays(vacationSummary?.remaining_days)} />
              <DetailCard icon={CheckCircle2} label="Consumidas" value={formatDays(vacationSummary?.used_days)} />
              <DetailCard icon={Clock3} label="Pendientes" value={formatDays(pendingVacationDays)} />
            </div>
            <div className="mt-4 rounded-[22px] border border-slate-200 bg-slate-50/70 px-4 py-3">
              <DataRow label="Base anual" value={formatDays(vacationSummary?.annual_days)} />
              <DataRow
                label="Próxima ausencia aprobada"
                value={nextApprovedVacation
                  ? `${formatDate(nextApprovedVacation.fecha_inicio)}${nextApprovedVacation.fecha_fin !== nextApprovedVacation.fecha_inicio ? ` - ${formatDate(nextApprovedVacation.fecha_fin)}` : ""}`
                  : "Sin próximas vacaciones"}
              />
            </div>
          </Section>
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,.9fr)]">
          <Section
            title="Mis tareas"
            subtitle="Prioridades personales próximas."
            icon={ListTodo}
            action={(
              <Link to="/tasks" className="inline-flex items-center gap-1 text-xs font-black text-emerald-700 hover:text-emerald-800">
                Ver tareas <ChevronRight size={14} />
              </Link>
            )}
          >
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-[20px] border border-slate-200 bg-slate-50/70 px-4 py-3">
                <div className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-400">Pendientes</div>
                <div className="mt-1 text-2xl font-black text-slate-950">{openTasks.length}</div>
              </div>
              <div className="rounded-[20px] border border-amber-200 bg-amber-50/70 px-4 py-3">
                <div className="text-[10px] font-black uppercase tracking-[0.16em] text-amber-600">Para hoy</div>
                <div className="mt-1 text-2xl font-black text-amber-800">{todayTasks.length}</div>
              </div>
              <div className="rounded-[20px] border border-rose-200 bg-rose-50/70 px-4 py-3">
                <div className="text-[10px] font-black uppercase tracking-[0.16em] text-rose-600">Vencidas</div>
                <div className="mt-1 text-2xl font-black text-rose-800">{overdueTasks.length}</div>
              </div>
            </div>

            <div className="mt-4 overflow-hidden rounded-[22px] border border-slate-200 bg-white">
              {nextTasks.length > 0 ? nextTasks.map((task) => (
                <div key={task.id} className="flex items-center justify-between gap-4 border-b border-slate-100 px-4 py-3 last:border-b-0">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-black text-slate-900" title={task.titulo}>{task.titulo}</div>
                    <div className="mt-0.5 text-xs font-semibold text-slate-400">{taskDueLabel(task)}</div>
                  </div>
                  <span className="shrink-0 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-[10px] font-black uppercase text-emerald-700">
                    {task.estado === "progreso" ? "En progreso" : task.estado === "revision" ? "Revisión" : "Pendiente"}
                  </span>
                </div>
              )) : (
                <div className="px-4 py-7 text-center text-sm font-semibold text-slate-400">No tienes tareas pendientes.</div>
              )}
            </div>
          </Section>

          <Section title="Información de cuenta" subtitle="Datos personales y laborales." icon={UserRound}>
            <div className="grid gap-x-6 md:grid-cols-2">
              <div>
                <DataRow label="Correo" value={user?.email ?? "Sin registrar"} />
                <DataRow label="Fecha de nacimiento" value={formatDate(user?.fecha_nacimiento)} />
                <DataRow label="Rol" value={user?.rol ?? "Sin registrar"} />
              </div>
              <div>
                <DataRow label="Departamento" value={user?.departamento ?? "Sin registrar"} />
                <DataRow label="Empresa" value={user?.empresa ?? "Sin registrar"} />
                <DataRow label="Inicio contratación" value={formatDate(user?.fecha_inicio_contratacion)} />
              </div>
            </div>
          </Section>
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.25fr)_minmax(0,.75fr)]">
          <Section title="Accesos y permisos" subtitle="Capacidades efectivas de tu cuenta." icon={Shield}>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {(me?.roles ?? []).map((role) => (
                <span key={role} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-black text-slate-700">
                  Rol · {role}
                </span>
              ))}
            </div>

            {accessSummary.length > 0 ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {accessSummary.map((item) => (
                  <div key={item.label} className="flex items-center justify-between gap-3 rounded-[20px] border border-[#dce9e3] bg-white px-4 py-3">
                    <span className="text-sm font-black text-slate-800">{item.label}</span>
                    <span className={[
                      "rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide",
                      item.level === "Gestión" ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-800",
                    ].join(" ")}>{item.level}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-5 text-sm font-semibold text-slate-500">
                Tu cuenta utiliza los accesos básicos asignados a su rol.
              </div>
            )}
          </Section>

          <div className="grid gap-6">
            <Section title="Actividad" subtitle="Trazabilidad reciente." icon={Clock3}>
              <DataRow label="Última actividad" value={formatDateTime(user?.last_activity)} />
              <DataRow label="Cuenta creada" value={formatDateTime(user?.creado_en)} />
            </Section>

            <Section title="Acciones rápidas" subtitle="Accesos habituales." icon={Sparkles}>
              <div className="grid gap-2">
                <button
                  type="button"
                  onClick={handleEdit}
                  className="flex w-full items-center justify-between rounded-[18px] border border-emerald-100 bg-emerald-50/70 px-4 py-3 text-left text-sm font-black text-emerald-800 transition hover:bg-emerald-100"
                >
                  Editar perfil <PencilLine size={16} />
                </button>
                <Link to="/rrhh" className="flex items-center justify-between rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 transition hover:bg-slate-50">
                  Recursos Humanos <ChevronRight size={16} />
                </Link>
                <Link to="/tasks" className="flex items-center justify-between rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 transition hover:bg-slate-50">
                  Tareas <ChevronRight size={16} />
                </Link>
                <Link to="/notifications" className="flex items-center justify-between rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700 transition hover:bg-slate-50">
                  Notificaciones <ChevronRight size={16} />
                </Link>
              </div>
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}
